@if (selectedId !== null) {
  <button type="button" class="btn btn-secondary" (click)="backToList()" [disabled]="confirming() || saving()">← Back to applications</button>
  @if (detailLoading()) { <p role="status" class="applications-state">Loading application details…</p> }
  @if (detailError()) { <p role="alert" class="alert alert-danger">{{ detailError() }}</p> }
  @if (successMessage()) { <p role="status" class="alert alert-success">{{ successMessage() }}</p> }
  @if (selected(); as review) {
    <header class="review-header">
      <div><h2>{{ review.application.referenceNumber }}</h2><p>{{ review.application.scholarship?.title }}</p></div>
      <span class="badge badge-primary">{{ review.application.applicationStatus?.name }}</span>
    </header>
    <section class="review-section" aria-labelledby="application-details-title">
      <h3 id="application-details-title">Details</h3>
      <dl class="details-grid">
        <div><dt>Applicant</dt><dd>{{ applicantName(review.application) }}</dd></div>
        <div><dt>Email</dt><dd>{{ review.application.user?.emailAddress || 'Not provided' }}</dd></div>
        <div><dt>Phone</dt><dd>{{ review.application.user?.phoneNumber || 'Not provided' }}</dd></div>
        <div><dt>Identity number</dt><dd>{{ review.application.user?.identityNumber || 'Not provided' }}</dd></div>
        <div><dt>Submitted</dt><dd>{{ review.application.applicationDate | date: 'dd MMM yyyy, HH:mm' }}</dd></div>
        <div>
          <dt>Declaration</dt>
          <dd>
            @if (review.declarationAccepted) {
              <span class="badge badge-success">Signed</span>
              @if (review.declarationAcceptedDate) {
                <span class="declaration-stamp">Accepted {{ review.declarationAcceptedDate | date: 'dd MMM yyyy, HH:mm' }}</span>
              }
            } @else {
              <span class="badge badge-danger">Not signed</span>
              <span class="declaration-stamp">This application has no recorded declaration.</span>
            }
          </dd>
        </div>
        @if (review.application.statusChangedDate) {
          <div><dt>Last decision</dt><dd>{{ review.application.statusChangedDate | date: 'dd MMM yyyy, HH:mm' }}</dd></div>
        }
      </dl>
      @for (category of review.details; track $index) {
        <h4>{{ category.categoryName }}</h4>
        <dl class="details-grid">
          @for (field of category.fieldResponses; track $index) {
            <div><dt>{{ field.fieldLabel || field.fieldName || field.fieldKey }}</dt><dd>{{ displayValue(field.fieldDisplayValue ?? field.fieldValue) }}</dd></div>
          }
        </dl>
      } @empty { <p>No additional application details were submitted.</p> }
    </section>
    <section class="review-section" aria-labelledby="application-documents-title">
      <h3 id="application-documents-title">Documents</h3>
      @for (document of review.documents; track document.id) {
        <article class="document-card">
          <div><h4>{{ document.name }}</h4><p>{{ document.documentType }} · {{ document.reviewStatus }}</p>
            @if (document.reviewedDate) { <small>Reviewed {{ document.reviewedDate | date: 'dd MMM yyyy, HH:mm' }}</small> }
          </div>
          <div class="review-actions">
            <button type="button" class="btn btn-secondary" [disabled]="downloading() !== null" (click)="downloadDocument(document.id, document.name)">{{ downloading() === document.id ? 'Downloading…' : 'Download' }}</button>
            <button type="button" class="btn btn-success" [disabled]="confirming() || saving() || document.reviewStatus === 'Approved'" (click)="reviewDocument(document.id, 'Approved')" [attr.aria-label]="'Approve document ' + document.name">Approve document</button>
            <button type="button" class="btn btn-danger" [disabled]="confirming() || saving() || document.reviewStatus === 'Rejected'" (click)="reviewDocument(document.id, 'Rejected')" [attr.aria-label]="'Reject document ' + document.name">Reject document</button>
          </div>
        </article>
      } @empty { <p>No documents were submitted with this application.</p> }
    </section>
    <section class="review-section" aria-labelledby="application-decision-title" [attr.aria-busy]="saving()">
      <h3 id="application-decision-title">Application decision</h3>
      @if (applicationDecided) {
        <p>This application has been {{ review.application.applicationStatus?.name | lowercase }}. The decision is final.</p>
      } @else {
      <div class="review-actions">
        <button type="button" class="btn btn-success" (click)="reviewApplication('Approve')" [disabled]="confirming() || saving() || !auth.hasPermission(permissions.ApplicationApprove)">Approve</button>
        <button type="button" class="btn btn-danger" (click)="reviewApplication('Decline')" [disabled]="confirming() || saving() || !auth.hasPermission(permissions.ApplicationReject)">Decline</button>
        <button type="button" class="btn btn-secondary" (click)="reviewApplication('HigherReview')" [disabled]="confirming() || saving()">Send to higher review</button>
      </div>
      @if (saving()) { <p role="status">Saving decision…</p> }
      }
    </section>
  }
} @else {
<div class="applications-toolbar">
  <label for="application-page-size">Applications per page</label>
  <select id="application-page-size" class="form-control" [(ngModel)]="pageSize" (ngModelChange)="changePageSize()">`
    @for (size of pageSizeOptions; track size) {
      <option [ngValue]="size">{{ size }}</option>
    }
  </select>
  <button type="button" class="btn btn-secondary" (click)="loadApplications()" [disabled]="loading">
    <i class="fas fa-sync-alt"></i> Refresh
  </button>
</div>

@if (loading) {
  <div class="applications-state">Loading applications…</div>
}

@if (!loading && errorMessage) {
  <div class="alert alert-danger">{{ errorMessage }}</div>
}

@if (!loading && !errorMessage) {
  <div class="table-responsive">
  <table class="table">
    <thead>
      <tr>
        <th>Reference</th>
        <th>Applicant</th>
        <th>Scholarship</th>
        <th>Submitted</th>
        <th>Status</th>
        <th>Actions</th>
      </tr>
    </thead>
    <tbody>
      @if (applications.length === 0) {
        <tr>
          <td colspan="6" class="applications-state">No applications found. Applications will appear here once students begin applying.</td>
        </tr>
      }
      @for (application of paginatedApplications; track application.id) {
        <tr class="application-row" (click)="openApplication(application)">
          <td><button type="button" class="reference-link" (click)="$event.stopPropagation(); openApplication(application)">{{ application.referenceNumber }}</button></td>
          <td>
            {{ applicantName(application) }}
            @if (application.user; as user) {
              @if (user.emailAddress) {
                <small>{{ user.emailAddress }}</small>
              }
            }
          </td>
          <td>{{ application.scholarship?.title || 'Unknown scholarship' }}</td>
          <td>{{ application.applicationDate | date: 'dd MMM yyyy, HH:mm' }}</td>
          <td><span class="badge badge-primary">{{ application.applicationStatus?.name || 'Unknown' }}</span></td>
          <td><button type="button" class="btn btn-secondary" (click)="$event.stopPropagation(); openApplication(application)" [attr.aria-label]="'View application ' + application.referenceNumber">View</button></td>
        </tr>
      }
    </tbody>
  </table>
  </div>

  <app-pagination [currentPage]="currentPage" [totalPages]="totalPages" [totalItems]="applications.length"
    [showingFrom]="showingFrom" [showingTo]="showingTo" itemLabel="applications" (pageChange)="goToPage($event)">
  </app-pagination>
}

}
